import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '.';
import { Profile } from '../models/profile';
import { getProfile } from '../services/api/profileServices';
import { EmployeeRole } from '../models/employee';

export const getProfileThunk = createAsyncThunk<Profile, void>(
  'userId',
  async () => {
    const profile = await getProfile();

    return profile;
  }
);

export interface ProfileState {
  profile: Profile | null;
  isLoading: boolean;
  error: unknown | null;
}

const initialState: ProfileState = {
  profile: null,
  isLoading: true,
  error: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    updateCurrentUserRoles: (state, action) => {
      if (state.profile) {
        state.profile.permissionGroups = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfileThunk.pending, (state) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(getProfileThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = false;
        state.profile = action.payload;
      })
      .addCase(getProfileThunk.rejected, (state, payload) => {
        state.isLoading = false;
        state.error = payload.error;
      });
  },
});

export const profileReducer = profileSlice.reducer;

export const { updateCurrentUserRoles } = profileSlice.actions;

// selectors
export const selectProfile = (root: RootState): Profile | null =>
  root.profile.profile;

export const selectUserLocation = (root: RootState): string | null => {
  if (!root.profile.profile) return null;

  return root.profile.profile.location;
};

export const selectUserRoles = (root: RootState): EmployeeRole[] => {
  if (!root.profile.profile) return [];

  return root.profile.profile.permissionGroups;
};

export const selectProfileId = (root: RootState): string | undefined =>
  root.profile.profile?.id;

export const selectErrorProfile = (root: RootState): unknown | null =>
  root.profile.error;
export const selectLoadingProfile = (root: RootState): boolean =>
  root.profile.isLoading;
export const selectProfileWorkingHours = (root: RootState): number =>
  root.profile.profile ? root.profile.profile.workingHours : 0;
